import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Play, Lock, CheckCircle2, ChevronDown, ChevronUp,
  Loader2, Award, ListChecks, AlertCircle, Trophy, Clock, Download,
  FileText, Sparkles, ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ACCENT = "#22D3EE";
const ACCENT_DEEP = "#0891B2";

export default function LearnPage() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token");

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [activeView, setActiveView] = useState("video"); // video | quiz | exam | cert
  const [activeModuleId, setActiveModuleId] = useState(null);
  const [openModuleIds, setOpenModuleIds] = useState(new Set());
  const [resourceTab, setResourceTab] = useState("resources");
  const [showHelpBanner, setShowHelpBanner] = useState(true);
  const [artifacts, setArtifacts] = useState([]);
  const [capstones, setCapstones] = useState([]);
  const videoBlockRef = useRef(null);

  async function load() {
    setLoading(true); setErr("");
    try {
      const res = await axios.get(`${API}/learn/${slug}?token=${token}`);
      setState(res.data);
      // A module is "done" when its quiz is passed, OR (for quiz-less Quick Bites)
      // the video has been watched ≥ 90 %. Land the learner on the first not-done
      // unlocked module so completing a video auto-advances to the next one.
      const isDone = (m) => m.has_quiz ? !!m.quiz_passed : (m.video_watched_pct || 0) >= 100;
      const first = res.data.modules.find((m) => m.unlocked && !isDone(m)) || res.data.modules[0];
      if (first) {
        setActiveModuleId(first.module_id);
        setOpenModuleIds(new Set([first.module_id]));
      }
      // Persist enrollment locally so the /courses dashboard can show Continue Learning
      try {
        const all = JSON.parse(localStorage.getItem("shansolearn_enrollments") || "{}");
        all[slug] = {
          slug,
          token,
          full_name: res.data.enrollment?.full_name,
          email: res.data.enrollment?.email,
          course_id: res.data.course?.course_id,
          course_title: res.data.course?.title,
          course_cover: res.data.course?.cover_image_url,
          last_visited: new Date().toISOString(),
        };
        localStorage.setItem("shansolearn_enrollments", JSON.stringify(all));
      } catch (_) { /* ignore quota errors */ }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load course.");
    } finally { setLoading(false); }
  }

  useEffect(() => { if (token) load(); else setErr("Access token missing. Open the link from your email."); }, [token, slug]);

  // Agentic AI Expert ships 5 downloadable agent-building PDF templates;
  // Excel AI Mastery ships 1 bonus Excel AI Prompt Library PDF.
  useEffect(() => {
    if (slug === "agentic-ai-expert" && token) {
      axios.get(`${API}/courses/agentic-ai-expert/artifacts?token=${token}`)
        .then((r) => setArtifacts(r.data?.items || []))
        .catch(() => { /* templates list is optional */ });
    } else if (slug === "qb-excel-ai-mastery" && token) {
      axios.get(`${API}/courses/qb-excel-ai-mastery/artifacts?token=${token}`)
        .then((r) => setArtifacts(r.data?.items || []))
        .catch(() => { /* artefact list is optional */ });
    }
  }, [slug, token]);

  // AI Powered Data Analyst, LangGraph Mastery, and Python Using AI ship capstone projects with AI-graded review.
  useEffect(() => {
    if (slug === "ai-powered-data-analyst" && token) {
      axios.get(`${API}/courses/ai-powered-data-analyst/capstones?token=${token}`)
        .then((r) => setCapstones(r.data?.items || []))
        .catch(() => { /* capstone list is optional */ });
    } else if (slug === "langgraph-mastery" && token) {
      axios.get(`${API}/courses/langgraph-mastery/capstones?token=${token}`)
        .then((r) => setCapstones(r.data?.items || []))
        .catch(() => { /* capstone list is optional */ });
    } else if (slug === "python-using-ai" && token) {
      axios.get(`${API}/courses/python-using-ai/capstones?token=${token}`)
        .then((r) => setCapstones(r.data?.items || []))
        .catch(() => { /* capstone list is optional */ });
    } else if (slug === "ai-era-data-scientist" && token) {
      axios.get(`${API}/courses/ai-era-data-scientist/capstones?token=${token}`)
        .then((r) => setCapstones(r.data?.items || []))
        .catch(() => { /* capstone list is optional */ });
    }
  }, [slug, token]);

  const overallPct = useMemo(() => {
    if (!state?.modules?.length) return 0;
    // Each module = 2 items (lecture + quiz). Done if watched = 100% / quiz passed.
    let done = 0; let total = 0;
    for (const m of state.modules) {
      total += m.has_quiz ? 2 : 1;
      if ((m.video_watched_pct || 0) >= 100) done += 1;
      if (m.has_quiz && m.quiz_passed) done += 1;
    }
    if (state.exam_passed) done += 0; // exam not in module pct
    return Math.round((done / total) * 100);
  }, [state]);

  const totalElapsedMin = useMemo(() => {
    if (!state?.modules) return 0;
    let secs = 0;
    for (const m of state.modules) {
      const dur = (m.duration_min || 0) * 60;
      secs += Math.round(dur * ((m.video_watched_pct || 0) / 100));
    }
    return Math.round(secs / 60);
  }, [state]);

  function toggleModuleOpen(id) {
    setOpenModuleIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  function selectLecture(moduleId) {
    setActiveModuleId(moduleId);
    setActiveView("video");
    // Scroll video into view
    setTimeout(() => videoBlockRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }

  function selectQuiz(moduleId) {
    setActiveModuleId(moduleId);
    setActiveView("quiz");
    setTimeout(() => videoBlockRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }

  if (loading) return <CenterMsg>Loading course…</CenterMsg>;
  if (err) return <CenterMsg>{err}</CenterMsg>;
  if (!state) return null;

  const activeModule = state.modules.find((m) => m.module_id === activeModuleId);
  const moduleNo = activeModule?.module_no;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* ─── Top Course Header Strip ─────────────────────────── */}
      <header
        className="sticky top-0 z-30 border-b border-cyan-500/20"
        style={{ background: "linear-gradient(90deg, #06222a 0%, #082a35 100%)" }}
      >
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/courses"
              className="shrink-0 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-cyan-200"
              data-testid="learn-back-btn"
            >
              <ArrowLeft size={16} />
            </Link>
            <p className="text-white text-sm sm:text-base font-bold truncate" data-testid="course-title">
              {state.course.title}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="px-3 py-1.5 rounded-md bg-white/5 border border-cyan-500/30 text-cyan-100 text-xs font-bold inline-flex items-center gap-1.5" data-testid="elapsed-time">
              <Clock size={12} /> {totalElapsedMin}m
            </div>
            <FinalAssessmentHeaderButton
              state={state}
              onOpenExam={() => { setActiveView("exam"); setTimeout(() => videoBlockRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }}
              onViewCert={() => { setActiveView("cert"); setTimeout(() => videoBlockRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }}
            />
            <div className="px-3 py-1.5 rounded-md text-black text-xs font-bold inline-flex items-center gap-1" style={{ background: ACCENT }} data-testid="overall-progress-pct">
              {overallPct}%
            </div>
          </div>
        </div>
      </header>

      {/* ─── Help Banner ─────────────────────────────────────── */}
      {showHelpBanner && (
        <div className="bg-amber-500/15 border-b border-amber-500/30">
          <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-start sm:items-center justify-between gap-3">
            <p className="text-amber-100 text-xs sm:text-sm leading-snug">
              Facing any issues with lectures or technical doubts? Email us at <a href="mailto:support@shansoai.com" className="font-bold underline">support@shansoai.com</a>
            </p>
            <button
              onClick={() => setShowHelpBanner(false)}
              className="text-amber-200 hover:text-white shrink-0 text-lg leading-none px-1"
              data-testid="dismiss-help-banner"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 space-y-4">
        {/* ─── Active Lesson Block (Video / Quiz / Exam / Cert) ─── */}
        <section ref={videoBlockRef} data-testid="active-lesson-block">
          {activeView === "exam" ? (
            <FinalExamView slug={slug} token={token} onPassed={() => { load(); setActiveView("cert"); }} />
          ) : activeView === "cert" ? (
            <CertificateView slug={slug} token={token} certificateId={state.certificate_id} scorePct={state.best_exam_score_pct ?? state.last_exam_score_pct} />
          ) : activeView === "quiz" ? (
            <ModuleQuizView slug={slug} token={token} module={activeModule} onDone={() => { load(); setActiveView("video"); }} />
          ) : (
            <ModuleVideoView
              slug={slug} token={token} module={activeModule}
              onTakeQuiz={() => setActiveView("quiz")}
              onCompleted={() => load()}
            />
          )}
        </section>

        {/* ─── Current Lecture Highlight Card ─── */}
        {activeView === "video" && activeModule && (
          <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/[0.06] p-4" data-testid="current-lecture-card">
            <p className="text-cyan-300 text-lg sm:text-xl font-extrabold leading-tight">
              Module {moduleNo}. {activeModule.title}
            </p>
            {activeModule.summary && (
              <p className="text-zinc-300 text-sm mt-1.5">{activeModule.summary}</p>
            )}
          </div>
        )}

        {/* ─── Course Resources Card ─── */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid="course-resources-card">
          <p className="text-white text-base font-bold mb-3">Course Resources</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setResourceTab("resources")}
              className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                resourceTab === "resources"
                  ? "bg-cyan-500/15 border-cyan-500/60 text-cyan-200"
                  : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
              }`}
              data-testid="resource-tab-resources"
            >
              Resources
            </button>
            <button
              onClick={() => setResourceTab("certificate")}
              className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                resourceTab === "certificate"
                  ? "bg-cyan-500/15 border-cyan-500/60 text-cyan-200"
                  : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
              }`}
              data-testid="resource-tab-certificate"
            >
              Certificate & Refund
            </button>
          </div>
          {resourceTab === "resources" ? (
            <div className="mt-4 space-y-2">
              {artifacts.length > 0 && (
                <div data-testid="agentic-templates-section">
                  <p className="text-xs font-bold uppercase tracking-wider text-violet-300 mb-2">
                    {slug === "qb-excel-ai-mastery"
                      ? "🎁 Bonus prompt library — yours to keep"
                      : "🎁 Agent-building templates — yours to keep"}
                  </p>
                  <div className="space-y-2">
                    {artifacts.map((a) => (
                      <a
                        key={a.slug}
                        href={`${API}/courses/${slug}/artifact/${a.slug}?token=${token}`}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-violet-950/30 border border-violet-700/40 hover:border-violet-500/70 transition-colors group"
                        data-testid={`artifact-${a.slug}`}
                      >
                        <div className="w-9 h-9 rounded-md bg-violet-500/10 border border-violet-500/30 flex items-center justify-center text-violet-300 shrink-0">
                          <Download size={16} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-semibold truncate group-hover:text-violet-200">{a.title}</p>
                          <p className="text-zinc-500 text-xs">{slug === "qb-excel-ai-mastery" ? `Bonus PDF · 25 prompts across all 5 modules` : `Module ${a.module} companion · PDF download`}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {capstones.length > 0 && (
                <div data-testid="capstones-section">
                  <p className="text-xs font-bold uppercase tracking-wider text-teal-300 mb-2">
                    🎯 Capstone projects — pick one, build it, get AI-graded
                  </p>
                  <div className="space-y-3">
                    {capstones.map((c) => (
                      <CapstoneCard key={c.slug} capstone={c} token={token} courseSlug={slug} />
                    ))}
                  </div>
                </div>
              )}
              {artifacts.length === 0 && capstones.length === 0 && (
                <div className="flex items-start gap-3 px-3 py-3 rounded-lg bg-zinc-950/40 border border-zinc-800" data-testid="no-extras">
                  <div className="w-9 h-9 rounded-md bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-300 shrink-0">
                    <Award size={16} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold leading-tight">Your certificate is the takeaway</p>
                    <p className="text-zinc-500 text-xs mt-0.5">
                      Finish the modules and pass the final assessment to unlock your shareable certificate. Bonus handbooks &amp; templates may arrive in your inbox separately when we ship them — keep an eye on <span className="text-cyan-300">support@shansoai.com</span>.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-4 space-y-2 text-zinc-300 text-sm leading-relaxed">
              <p><b className="text-white">Certificate:</b> Issued automatically after you pass the final assessment with {state.exam_pass_pct || 80}% or more.</p>
              <p><b className="text-white">Refund policy:</b> All sales are final — no refunds once payment is confirmed. <Link to="/refund-policy" className="text-cyan-300 hover:underline">Read full policy →</Link></p>
            </div>
          )}
        </div>

        {/* ─── Module List (Accordion Cards) ─── */}
        <div className="space-y-3" data-testid="modules-accordion">
          {state.modules.map((m, idx) => {
            const open = openModuleIds.has(m.module_id);
            const isActive = m.module_id === activeModuleId && activeView !== "exam" && activeView !== "cert";
            const itemsTotal = m.has_quiz ? 2 : 1;
            const itemsDone = ((m.video_watched_pct || 0) >= 100 ? 1 : 0) + (m.has_quiz && m.quiz_passed ? 1 : 0);
            return (
              <ModuleCard
                key={m.module_id}
                module={m}
                open={open}
                active={isActive}
                itemsDone={itemsDone}
                itemsTotal={itemsTotal}
                onToggle={() => toggleModuleOpen(m.module_id)}
                onSelectLecture={() => selectLecture(m.module_id)}
                onSelectQuiz={() => selectQuiz(m.module_id)}
              />
            );
          })}
        </div>

        {/* ─── Final Assessment Card ─── */}
        <FinalAssessmentCard
          state={state}
          onStart={() => { setActiveView("exam"); setTimeout(() => videoBlockRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }}
          onViewCert={() => { setActiveView("cert"); setTimeout(() => videoBlockRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }}
        />
      </main>
    </div>
  );
}

/* ─── Reusable bits ──────────────────────────────────────── */

function CenterMsg({ children }) {
  return <div className="min-h-screen bg-[#0a0a0a] text-zinc-400 flex items-center justify-center p-6 text-center">{children}</div>;
}

function FinalAssessmentHeaderButton({ state, onOpenExam, onViewCert }) {
  const [showReason, setShowReason] = useState(false);
  const hasCert = !!state.certificate_id;
  const passed = !!state.exam_passed;
  const available = !!state.exam_available;
  const reason = state.exam_lock_reason || "Finish every module first to unlock the final assessment.";
  if (passed && hasCert) {
    return (
      <button
        onClick={onViewCert}
        className="px-3 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-400/50 text-emerald-200 text-xs font-bold inline-flex items-center gap-1.5 hover:bg-emerald-500/25 transition-colors"
        title="View your certificate"
        data-testid="header-view-cert-btn"
      >
        <Award size={12} /> Certificate
      </button>
    );
  }
  if (available) {
    return (
      <button
        onClick={onOpenExam}
        className="px-3 py-1.5 rounded-md text-black text-xs font-extrabold inline-flex items-center gap-1.5 shadow-[0_0_0_2px_rgba(245,158,11,0.25)] hover:shadow-[0_0_0_3px_rgba(245,158,11,0.4)] transition-shadow"
        style={{ background: "linear-gradient(90deg, #FBBF24, #F59E0B)" }}
        title="Start the final assessment"
        data-testid="header-start-exam-btn"
      >
        <Trophy size={12} /> Final Assessment
      </button>
    );
  }
  // Locked: clickable, opens a small reason popover so learners aren't blocked silently.
  return (
    <div className="relative">
      <button
        onClick={() => setShowReason((v) => !v)}
        className="px-3 py-1.5 rounded-md bg-white/5 border border-zinc-700 text-zinc-300 text-xs font-bold inline-flex items-center gap-1.5 hover:bg-white/10 transition-colors"
        title="Why is this locked?"
        data-testid="header-locked-exam-btn"
      >
        <Lock size={11} /> Final Assessment
      </button>
      {showReason && (
        <div
          className="absolute right-0 top-full mt-2 w-72 sm:w-80 rounded-lg border border-amber-500/40 bg-zinc-950/95 backdrop-blur p-3 z-40 shadow-xl"
          data-testid="header-locked-reason"
        >
          <p className="text-amber-300 text-[11px] uppercase tracking-wider font-bold mb-1">Why is this locked?</p>
          <p className="text-zinc-200 text-xs leading-relaxed">{reason}</p>
          <button
            onClick={() => setShowReason(false)}
            className="mt-2 text-cyan-300 text-[11px] hover:underline"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}

function ModuleCard({ module: m, open, active, itemsDone, itemsTotal, onToggle, onSelectLecture, onSelectQuiz }) {
  const locked = !m.unlocked;
  const lectureDone = (m.video_watched_pct || 0) >= 100;
  return (
    <div
      className={`rounded-xl overflow-hidden border transition-colors ${
        active ? "border-cyan-500/60 bg-cyan-500/[0.06]" : "border-zinc-800 bg-zinc-900/40"
      }`}
      data-testid={`module-card-${m.module_no}`}
    >
      <button
        onClick={onToggle}
        disabled={locked}
        className={`w-full flex items-start justify-between gap-3 p-4 text-left ${locked ? "opacity-60 cursor-not-allowed" : "hover:bg-white/[0.02]"}`}
        data-testid={`module-toggle-${m.module_no}`}
      >
        <div className="min-w-0 flex-1">
          <p className={`text-base sm:text-lg font-extrabold leading-tight ${active ? "text-cyan-300" : "text-white"}`}>
            Module {m.module_no}: {m.title}
          </p>
          <p className="text-zinc-400 text-xs mt-1 inline-flex items-center gap-2 flex-wrap">
            {m.duration_min ? <span>{m.duration_min}m</span> : null}
            <span className="text-zinc-600">|</span>
            <span>{itemsDone} / {itemsTotal} {itemsTotal === 1 ? "lecture" : "items"}</span>
            {locked && <span className="inline-flex items-center gap-1 text-zinc-500"><Lock size={11} /> Locked</span>}
          </p>
        </div>
        <span className={`shrink-0 mt-1 ${active ? "text-cyan-300" : "text-zinc-400"}`}>
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>

      {open && !locked && (
        <div className="border-t border-zinc-800 px-3 pb-3 pt-2 space-y-1">
          {/* Lecture row */}
          <LectureRow
            label={`${m.module_no}. ${m.title}`}
            sub="Lecture"
            done={lectureDone}
            playing={active}
            onClick={onSelectLecture}
            testid={`lecture-row-${m.module_no}`}
          />
          {/* Quiz row (assignment style) */}
          {m.has_quiz && (
            <QuizRow
              label={`Module ${m.module_no} Quiz`}
              passed={!!m.quiz_passed}
              score={m.best_quiz_score}
              onClick={onSelectQuiz}
              testid={`quiz-row-${m.module_no}`}
            />
          )}
        </div>
      )}
    </div>
  );
}

function LectureRow({ label, sub, done, playing, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
        playing ? "bg-cyan-500/10" : "hover:bg-white/[0.03]"
      }`}
      data-testid={testid}
    >
      <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
        playing ? "bg-cyan-500/30 text-cyan-100" : "bg-zinc-800 text-cyan-300"
      }`}>
        <Play size={12} className={playing ? "" : "ml-[1px]"} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-white text-sm font-medium truncate">{label}</p>
        {sub && <p className="text-zinc-500 text-[11px]">{sub}</p>}
      </div>
      <span className="shrink-0">
        {done ? (
          <CheckCircle2 size={18} className="text-emerald-400" />
        ) : (
          <span className="w-4 h-4 rounded-sm border-2 border-zinc-600 inline-block" />
        )}
      </span>
    </button>
  );
}

function QuizRow({ label, passed, score, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left hover:bg-white/[0.03]"
      data-testid={testid}
    >
      <span className="shrink-0 w-7 h-7 rounded-full bg-amber-500/15 text-amber-300 flex items-center justify-center">
        <ListChecks size={13} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-white text-sm font-medium truncate">{label}</p>
        <p className="text-zinc-500 text-[11px]">
          Assignment {passed ? `· Passed${score != null ? ` (${score}%)` : ""}` : "· Pending"}
        </p>
      </div>
      <span className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-bold ${passed ? "text-emerald-400" : "text-amber-300"}`}>
        <span className={`w-2 h-2 rounded-full ${passed ? "bg-emerald-400" : "bg-amber-400"}`} />
        {passed ? "Done" : "Pending"}
      </span>
    </button>
  );
}

function FinalAssessmentCard({ state, onStart, onViewCert }) {
  const hasCert = !!state.certificate_id;
  const passed = state.exam_passed;
  const available = state.exam_available;
  const lastAttempt = state.last_exam_attempt || null;
  const bestScore = state.best_exam_score_pct ?? lastAttempt?.score_pct ?? null;
  const lastScore = state.last_exam_score_pct ?? lastAttempt?.score_pct ?? null;
  const attemptsUsed = state.exam_attempts_used || 0;
  const attemptsRemaining = state.exam_attempts_remaining ?? null;
  const passPct = state.exam_pass_pct || 80;
  return (
    <div
      className={`rounded-xl border p-4 ${
        passed ? "border-emerald-500/40 bg-emerald-500/[0.06]" :
        available ? "border-amber-500/40 bg-amber-500/[0.06]" :
        "border-zinc-800 bg-zinc-900/40"
      }`}
      data-testid="final-assessment-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-white text-base sm:text-lg font-extrabold inline-flex items-center gap-2">
            {passed ? <Trophy size={18} className="text-amber-300" /> : <ListChecks size={18} className={available ? "text-amber-300" : "text-zinc-500"} />}
            Final Assessment
          </p>
          <p className="text-zinc-400 text-xs mt-1">
            {state.exam_total_questions || 30} questions · {passPct}% to pass · 45-min timer
          </p>
        </div>
        <span className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold ${
          passed ? "bg-emerald-500/20 text-emerald-200" :
          available ? "bg-amber-500/20 text-amber-200" :
          "bg-zinc-800 text-zinc-400"
        }`} data-testid="exam-status-badge">
          {passed ? "PASSED" : available ? "AVAILABLE" : "LOCKED"}
        </span>
      </div>

      {(bestScore != null || attemptsUsed > 0) && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="exam-score-strip">
          {bestScore != null && (
            <div className={`rounded-lg border p-2.5 ${passed ? "border-emerald-500/40 bg-emerald-500/10" : "border-amber-500/40 bg-amber-500/10"}`}>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">{passed ? "Your score" : "Best score"}</p>
              <p className={`text-2xl font-extrabold leading-none mt-1 ${passed ? "text-emerald-200" : "text-amber-200"}`} data-testid="exam-best-score">
                {bestScore}%
              </p>
              {lastAttempt && lastAttempt.correct != null && (
                <p className="text-[11px] text-zinc-400 mt-1">{lastAttempt.correct} / {lastAttempt.total} correct</p>
              )}
            </div>
          )}
          {!passed && lastScore != null && lastScore !== bestScore && (
            <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-2.5">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Last attempt</p>
              <p className="text-2xl font-extrabold leading-none mt-1 text-zinc-200" data-testid="exam-last-score">{lastScore}%</p>
            </div>
          )}
          {attemptsUsed > 0 && (
            <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-2.5">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Attempts</p>
              <p className="text-2xl font-extrabold leading-none mt-1 text-zinc-200" data-testid="exam-attempts">
                {attemptsUsed}{attemptsRemaining != null ? <span className="text-zinc-500 text-base"> / {attemptsUsed + attemptsRemaining}</span> : null}
              </p>
              {attemptsRemaining != null && !passed && (
                <p className="text-[11px] text-zinc-400 mt-1">{attemptsRemaining} remaining</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex gap-2 flex-wrap">
        {passed ? (
          hasCert ? (
            <button
              onClick={onViewCert}
              className="px-4 py-2 rounded-lg text-black font-bold text-sm"
              style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
              data-testid="view-cert-btn"
            >
              <Award size={14} className="inline mr-1" /> View certificate
            </button>
          ) : (
            <span className="text-emerald-300 text-sm">Certificate being issued…</span>
          )
        ) : available ? (
          <button
            onClick={onStart}
            disabled={attemptsRemaining === 0}
            className="px-4 py-2 rounded-lg text-black font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
            data-testid="start-final-exam-btn"
          >
            {attemptsUsed > 0 ? "Retry assessment" : "Begin assessment"} <ChevronRight size={14} className="inline" />
          </button>
        ) : (
          <p className="text-zinc-500 text-xs" data-testid="exam-lock-reason">
            {state.exam_lock_reason || "Finish every module first to unlock the final assessment."}
          </p>
        )}
      </div>
    </div>
  );
}

/* ─── Module Video Player ──────────────────────────────────── */

function ModuleVideoView({ slug, token, module: m, onTakeQuiz, onCompleted }) {
  const videoRef = useRef(null);
  const [pct, setPct] = useState(m?.video_watched_pct || 0);
  const lastSavedRef = useRef(m?.video_watched_pct || 0);
  const completedFiredRef = useRef((m?.video_watched_pct || 0) >= 100);

  useEffect(() => {
    setPct(m?.video_watched_pct || 0);
    lastSavedRef.current = m?.video_watched_pct || 0;
    completedFiredRef.current = (m?.video_watched_pct || 0) >= 100;
  }, [m?.module_id]);

  if (!m) {
    return (
      <div className="aspect-video bg-black rounded-xl border border-zinc-800 flex items-center justify-center text-zinc-500 text-sm">
        Select a module to start.
      </div>
    );
  }

  function savePct(p) {
    lastSavedRef.current = p;
    axios
      .post(`${API}/learn/${slug}/progress?token=${token}`, { module_id: m.module_id, video_watched_pct: p })
      .then(() => {
        // For quiz-less modules (Quick Bites), finishing the video (100%) unlocks
        // the next module on the backend — reload state so the UI reflects it.
        if (!m.has_quiz && !completedFiredRef.current && p >= 100) {
          completedFiredRef.current = true;
          onCompleted && onCompleted();
        }
      })
      .catch(() => {});
  }

  function onTimeUpdate(e) {
    const v = e.currentTarget;
    if (!v.duration) return;
    const p = Math.round((v.currentTime / v.duration) * 100);
    setPct(p);
    if (p >= lastSavedRef.current + 10) {
      savePct(p);
    }
  }

  function onEnded() {
    // The browser's timeupdate may round to 99 just before the end. Force 100
    // when the video finishes so the next module unlocks reliably.
    setPct(100);
    if (lastSavedRef.current < 100) savePct(100);
  }

  const videoSrc = `${API}/learn/${slug}/video/${m.module_id}?token=${token}`;

  return (
    <div className="space-y-3" data-testid="module-video-view">
      <div className="bg-black rounded-xl overflow-hidden border border-zinc-800 relative">
        <div className="aspect-video">
          <video
            ref={videoRef}
            src={videoSrc}
            controls
            preload="metadata"
            className="w-full h-full object-contain"
            onTimeUpdate={onTimeUpdate}
            onEnded={onEnded}
            data-testid="module-video"
          />
        </div>
        {/* Progress strip */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40">
          <div className="h-full bg-cyan-400 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
      {m.has_quiz ? (
        <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-zinc-900/40 border border-zinc-800 flex-wrap">
          <p className="text-zinc-300 text-xs">
            <Clock size={12} className="inline mr-1 mb-0.5" />{pct}% watched
            {pct >= 100 ? <span className="text-emerald-300 ml-2">· Ready for quiz</span> : <span className="text-zinc-500 ml-2">· Finish the video to unlock the quiz</span>}
          </p>
          <button
            onClick={onTakeQuiz}
            className="px-3 py-1.5 rounded-md text-black font-bold text-xs"
            style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
            data-testid="open-quiz-btn"
          >
            {m.quiz_passed ? "Re-take quiz" : "Take quiz"} <ChevronRight size={12} className="inline" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-zinc-900/40 border border-zinc-800 flex-wrap">
          <p className="text-zinc-300 text-xs">
            <Clock size={12} className="inline mr-1 mb-0.5" />{pct}% watched
            {pct >= 100
              ? <span className="text-emerald-300 ml-2">· Module complete — next one unlocked</span>
              : <span className="text-zinc-500 ml-2">· Finish the video to unlock the next module</span>}
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Module Quiz ───────────────────────────────────────────── */

function ModuleQuizView({ slug, token, module: m, onDone }) {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/learn/${slug}/quiz/${m.module_id}?token=${token}`)
      .then((r) => setQuiz(r.data.quiz))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load quiz."));
  }, [m?.module_id]);

  async function submit() {
    if (!quiz) return;
    if (Object.keys(answers).length !== quiz.length) { setErr("Answer every question first."); return; }
    setSubmitting(true); setErr("");
    try {
      const payload = { answers: quiz.map((_, i) => answers[i]) };
      const res = await axios.post(`${API}/learn/${slug}/quiz/${m.module_id}?token=${token}`, payload);
      setResult(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Quiz submission failed.");
    } finally { setSubmitting(false); }
  }

  if (err && !quiz) return <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{err}</div>;
  if (!quiz) return <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6 text-center text-zinc-400"><Loader2 size={18} className="animate-spin inline mr-2" /> Loading quiz…</div>;

  if (result) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 space-y-4" data-testid="quiz-result">
        <div className="text-center">
          {result.passed ? (
            <>
              <CheckCircle2 size={48} className="mx-auto text-emerald-400 mb-2" />
              <p className="text-2xl font-bold text-white">Passed — {result.score_pct}%</p>
              <p className="text-zinc-400 text-sm">{result.correct_count} / {result.total} correct · Next module unlocked.</p>
            </>
          ) : (
            <>
              <AlertCircle size={48} className="mx-auto text-amber-400 mb-2" />
              <p className="text-2xl font-bold text-white">Almost — {result.score_pct}%</p>
              <p className="text-zinc-400 text-sm">You need {result.pass_threshold_pct}% to unlock the next module.</p>
            </>
          )}
        </div>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {result.per_question.map((q, i) => (
            <div key={i} className={`text-sm p-3 rounded-lg border ${q.is_correct ? "bg-emerald-500/10 border-emerald-500/30" : "bg-red-500/10 border-red-500/30"}`}>
              <p className="text-white font-semibold">{i + 1}. {q.q}</p>
              {!q.is_correct && q.explanation && <p className="text-zinc-400 text-xs mt-1.5">💡 {q.explanation}</p>}
            </div>
          ))}
        </div>
        <button onClick={onDone} className="w-full px-4 py-2.5 rounded-lg text-black font-bold text-sm" style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }} data-testid="quiz-continue-btn">
          Continue
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 space-y-4" data-testid="quiz-view">
      <div>
        <p className="text-zinc-400 text-[11px] uppercase tracking-wider font-bold">Module {m.module_no} · Quiz</p>
        <p className="text-white text-lg font-bold">{m.title}</p>
        <p className="text-zinc-400 text-xs mt-1">{quiz.length} questions · 60% to pass</p>
      </div>
      {quiz.map((q, qi) => (
        <div key={qi} className="bg-zinc-950/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-white text-[15px] font-semibold mb-3">{qi + 1}. {q.q}</p>
          <div className="space-y-1.5">
            {q.options.map((opt, oi) => (
              <label key={oi} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer border ${
                answers[qi] === oi ? "bg-cyan-500/10 border-cyan-500/40" : "border-zinc-800 hover:bg-zinc-900"
              }`} data-testid={`quiz-${qi}-opt-${oi}`}>
                <input type="radio" name={`q${qi}`} checked={answers[qi] === oi} onChange={() => setAnswers((a) => ({ ...a, [qi]: oi }))} className="accent-cyan-500" />
                <span className="text-zinc-300 text-sm">{opt}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      {err && <p className="text-amber-300 text-xs">{err}</p>}
      <button onClick={submit} disabled={submitting} className="w-full px-4 py-3 rounded-lg text-black font-bold text-sm disabled:opacity-60" style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }} data-testid="submit-quiz-btn">
        {submitting ? <><Loader2 size={14} className="animate-spin inline mr-1" /> Submitting…</> : "Submit quiz"}
      </button>
    </div>
  );
}

/* ─── Final Exam ────────────────────────────────────────────── */

function FinalExamView({ slug, token, onPassed }) {
  const [stage, setStage] = useState("intro");
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (stage !== "exam" || !attempt) return;
    const tick = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { clearInterval(tick); submit(); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
    // eslint-disable-next-line
  }, [stage, attempt]);

  async function start() {
    setErr("");
    try {
      const res = await axios.post(`${API}/learn/${slug}/exam/start?token=${token}`);
      setAttempt(res.data);
      setRemaining(res.data.time_limit_minutes * 60);
      setStage("exam");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start exam.");
    }
  }

  async function submit() {
    if (submitting) return;
    setSubmitting(true); setErr("");
    try {
      const payload = { attempt_id: attempt.attempt_id, answers: attempt.questions.map((_, i) => answers[i] ?? -1) };
      const res = await axios.post(`${API}/learn/${slug}/exam/submit?token=${token}`, payload);
      setResult(res.data);
      setStage("result");
      if (res.data.passed) setTimeout(() => onPassed(), 2200);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Submission failed.");
    } finally { setSubmitting(false); }
  }

  if (stage === "intro") {
    return (
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/[0.06] p-6 text-center" data-testid="exam-intro">
        <ListChecks size={40} className="mx-auto text-amber-300 mb-2" />
        <p className="text-white text-2xl font-bold">Final Assessment</p>
        <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">30 multiple-choice questions across every module · 80% to pass · 45-minute timer · 3 attempts total</p>
        {err && <p className="text-amber-300 text-xs mt-3">{err}</p>}
        <button onClick={start} className="mt-5 px-5 py-3 rounded-lg text-black font-bold text-sm" style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }} data-testid="start-exam-btn">
          Begin exam
        </button>
      </div>
    );
  }

  if (stage === "result") {
    return (
      <div className={`rounded-xl border p-6 text-center ${result.passed ? "border-emerald-500/40 bg-emerald-500/[0.06]" : "border-amber-500/40 bg-amber-500/[0.06]"}`} data-testid="exam-result">
        {result.passed ? <Trophy size={56} className="mx-auto text-amber-300 mb-3" /> : <AlertCircle size={56} className="mx-auto text-amber-300 mb-3" />}
        <p className="text-white text-3xl font-bold">{result.passed ? "You passed!" : "Almost there!"}</p>
        <p className="text-zinc-300 text-base mt-2">Score: <b className="text-white">{result.score_pct}%</b> ({result.correct} / {result.total})</p>
        {result.passed ? (
          <p className="text-emerald-300 text-sm mt-3">Your certificate has been issued and emailed. Redirecting…</p>
        ) : (
          <p className="text-zinc-400 text-sm mt-3">You need 80% to pass. Review your weak modules and try again.</p>
        )}
      </div>
    );
  }

  const min = Math.floor(remaining / 60);
  const sec = String(remaining % 60).padStart(2, "0");
  return (
    <div className="space-y-3" data-testid="exam-active">
      <div className="sticky top-16 z-10 bg-zinc-900/90 backdrop-blur border border-zinc-800 rounded-xl p-3 flex items-center justify-between">
        <p className="text-white text-sm font-bold">Final Exam · {attempt.total_questions} Qs</p>
        <p className={`text-sm font-bold inline-flex items-center gap-1.5 ${remaining < 120 ? "text-red-400" : "text-amber-300"}`}>
          <Clock size={14} /> {min}:{sec}
        </p>
      </div>
      {attempt.questions.map((q, qi) => (
        <div key={qi} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <p className="text-white text-[14px] font-semibold mb-2.5">{qi + 1}. {q.q}</p>
          <div className="space-y-1.5">
            {q.options.map((opt, oi) => (
              <label key={oi} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer border ${
                answers[qi] === oi ? "bg-cyan-500/10 border-cyan-500/40" : "border-zinc-800 hover:bg-zinc-900"
              }`} data-testid={`exam-${qi}-opt-${oi}`}>
                <input type="radio" name={`eq${qi}`} checked={answers[qi] === oi} onChange={() => setAnswers((a) => ({ ...a, [qi]: oi }))} className="accent-cyan-500" />
                <span className="text-zinc-300 text-sm">{opt}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      {err && <p className="text-amber-300 text-xs">{err}</p>}
      <button onClick={submit} disabled={submitting} className="w-full px-4 py-3 rounded-lg text-black font-bold text-sm disabled:opacity-60" style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }} data-testid="submit-exam-btn">
        {submitting ? <><Loader2 size={14} className="animate-spin inline mr-1" /> Submitting…</> : "Submit exam"}
      </button>
    </div>
  );
}

/* ─── Certificate View ──────────────────────────────────────── */

function CertificateView({ slug, token, certificateId, scorePct }) {
  if (!certificateId) {
    return <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6 text-center text-zinc-400">No certificate yet — pass the final assessment first.</div>;
  }
  const dl = `${API}/learn/${slug}/certificate?token=${token}`;
  return (
    <div className="rounded-xl border border-cyan-500/40 bg-cyan-500/[0.06] p-6 text-center" data-testid="cert-view">
      <Award size={56} className="mx-auto text-cyan-300 mb-3" />
      <p className="text-white text-3xl font-bold">Your certificate is ready</p>
      {scorePct != null && (
        <p className="text-zinc-200 text-base mt-3" data-testid="cert-view-score">
          You scored <b className="text-emerald-300 text-xl">{scorePct}%</b> in the final assessment
        </p>
      )}
      <p className="text-zinc-300 text-sm mt-2">Certificate ID: <span className="text-cyan-300 font-mono">{certificateId}</span></p>
      <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
        <a href={dl} className="inline-flex items-center gap-1.5 px-5 py-3 rounded-lg text-black font-bold text-sm" style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }} data-testid="download-cert-btn">
          <Download size={14} /> Download PDF
        </a>
        <Link to={`/verify-certificate/${certificateId}`} className="text-cyan-300 text-sm hover:underline">Verify certificate →</Link>
      </div>
      <p className="text-zinc-500 text-[11px] mt-4">A copy has also been emailed to you.</p>
    </div>
  );
}


/* ─── Capstone Card (AI Powered Data Analyst) ──────────────────────── */

function CapstoneCard({ capstone, token, courseSlug = "ai-powered-data-analyst" }) {
  const [expanded, setExpanded] = useState(false);
  const [submission, setSubmission] = useState("");
  const [grading, setGrading] = useState(false);
  const [grade, setGrade] = useState(null);
  const [err, setErr] = useState("");

  // Load any prior submission once when the card is first expanded
  useEffect(() => {
    if (!expanded || grade) return;
    axios.get(`${API}/courses/${courseSlug}/capstone/${capstone.slug}/my-submission?token=${token}`)
      .then((r) => {
        if (r.data?.submitted) {
          setSubmission(r.data.submission_text || "");
          if (r.data.grade) setGrade(r.data.grade);
        }
      })
      .catch(() => { /* fine */ });
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onSubmit() {
    setErr("");
    if (submission.trim().length < 500) {
      setErr("Please write at least 500 words — describe your approach, architecture, choices, and outcomes.");
      return;
    }
    setGrading(true);
    setGrade(null);
    try {
      const r = await axios.post(
        `${API}/courses/${courseSlug}/capstone/${capstone.slug}/submit?token=${token}`,
        { submission_text: submission },
      );
      setGrade(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "AI grading service is busy — please retry in a few seconds.");
    } finally {
      setGrading(false);
    }
  }

  const wordCount = submission.trim() ? submission.trim().split(/\s+/).length : 0;
  const isDataAnalyst = courseSlug === "ai-powered-data-analyst";
  const subTitle = isDataAnalyst
    ? `${capstone.industry} · ${(capstone.row_count || 0).toLocaleString()} rows`
    : capstone.track;
  const downloadHref = isDataAnalyst
    ? `${API}/courses/${courseSlug}/capstone/${capstone.slug}/dataset?token=${token}`
    : `${API}/courses/${courseSlug}/capstone/${capstone.slug}/starter?token=${token}`;
  const downloadLabel = isDataAnalyst
    ? `Download Dataset (CSV · ${(capstone.row_count || 0).toLocaleString()} rows)`
    : "Download Starter ZIP (Python boilerplate)";

  return (
    <div className="rounded-xl border border-teal-700/40 bg-teal-950/20 overflow-hidden" data-testid={`capstone-${capstone.slug}`}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-teal-950/40 transition-colors"
        data-testid={`capstone-toggle-${capstone.slug}`}
      >
        <div className="w-9 h-9 rounded-md bg-teal-500/10 border border-teal-500/30 flex items-center justify-center text-teal-300 shrink-0">
          <Trophy size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-semibold truncate">{capstone.title}</p>
          <p className="text-zinc-500 text-xs truncate">{subTitle}</p>
        </div>
        {grade && grade.pass != null && (
          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${grade.pass ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" : "bg-amber-500/15 text-amber-300 border border-amber-500/40"}`}>
            {grade.pass ? `Pass ${grade.total_score}/60` : `Resubmit ${grade.total_score}/60`}
          </span>
        )}
        {expanded ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
      </button>

      {expanded && (
        <div className="border-t border-teal-700/30 px-3 py-3 space-y-3" data-testid={`capstone-body-${capstone.slug}`}>
          <p className="text-zinc-300 text-xs leading-relaxed">
            <b className="text-teal-200">Track:</b> {capstone.track}
          </p>

          <div className="flex flex-wrap gap-2">
            <a
              href={`${API}/courses/${courseSlug}/capstone/${capstone.slug}/brief?token=${token}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-teal-500/10 border border-teal-500/40 text-teal-200 text-xs font-semibold hover:bg-teal-500/20"
              data-testid={`capstone-brief-${capstone.slug}`}
            >
              <FileText size={13} /> Download Brief (PDF)
            </a>
            <a
              href={downloadHref}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-fuchsia-500/10 border border-fuchsia-500/40 text-fuchsia-200 text-xs font-semibold hover:bg-fuchsia-500/20"
              data-testid={`capstone-download-${capstone.slug}`}
            >
              <Download size={13} /> {downloadLabel}
            </a>
          </div>

          <div className="pt-1">
            <p className="text-xs font-bold uppercase tracking-wider text-teal-300 mb-1.5">
              Submit your executive memo for AI grading (~60s)
            </p>
            <textarea
              value={submission}
              onChange={(e) => setSubmission(e.target.value)}
              rows={9}
              placeholder="Paste your 500-1,500 word executive memo here. Cover: data prep, modeling/AI approach, agent design, storytelling, and ₹ business impact with assumptions."
              className="w-full text-xs bg-zinc-950/60 border border-zinc-700 rounded-md p-2.5 text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-teal-500/60 resize-none"
              data-testid={`capstone-textarea-${capstone.slug}`}
            />
            <div className="flex items-center justify-between mt-1.5">
              <p className="text-[11px] text-zinc-500">
                {wordCount} words · min 500 · max ~1,500
              </p>
              <button
                onClick={onSubmit}
                disabled={grading || wordCount < 100}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-teal-500 hover:bg-teal-400 text-black text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`capstone-submit-${capstone.slug}`}
              >
                {grading ? <><Loader2 size={13} className="animate-spin" /> Grading…</> : <><Sparkles size={13} /> Submit for AI Review</>}
              </button>
            </div>
            {err && (
              <p className="text-xs text-red-400 mt-1.5" data-testid={`capstone-err-${capstone.slug}`}>{err}</p>
            )}
          </div>

          {grade && grade.scores && (
            <div className="mt-3 rounded-lg border border-teal-700/40 bg-zinc-950/60 p-3 space-y-2" data-testid={`capstone-grade-${capstone.slug}`}>
              <div className="flex items-baseline gap-3">
                <p className="text-white text-xl font-bold">{grade.total_score}<span className="text-zinc-500 text-sm font-normal">/{grade.max_score}</span></p>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${grade.pass ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" : "bg-amber-500/15 text-amber-300 border border-amber-500/40"}`}>
                  {grade.pass ? "Pass" : "Below pass — improve & resubmit"}
                </span>
              </div>
              {grade.verdict && <p className="text-zinc-300 text-xs leading-relaxed"><b className="text-teal-200">Verdict:</b> {grade.verdict}</p>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1">
                {Object.entries(grade.scores).map(([k, v]) => (
                  <div key={k} className="text-[11px] bg-zinc-900/60 rounded-md px-2 py-1.5 border border-zinc-800">
                    <div className="flex items-baseline justify-between">
                      <span className="text-zinc-400 capitalize">{k.replace(/_/g, " ")}</span>
                      <span className="text-teal-300 font-bold">{v.score}/10</span>
                    </div>
                    <p className="text-zinc-500 mt-0.5 leading-relaxed">{v.comment}</p>
                  </div>
                ))}
              </div>

              {(grade.top_strength || grade.biggest_gap) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                  {grade.top_strength && (
                    <p className="text-[11px] text-emerald-300/90 bg-emerald-500/[0.04] border border-emerald-500/20 rounded-md px-2 py-1.5"><b>Top strength:</b> {grade.top_strength}</p>
                  )}
                  {grade.biggest_gap && (
                    <p className="text-[11px] text-amber-300/90 bg-amber-500/[0.04] border border-amber-500/20 rounded-md px-2 py-1.5"><b>Biggest gap:</b> {grade.biggest_gap}</p>
                  )}
                </div>
              )}

              {grade.next_steps && grade.next_steps.length > 0 && (
                <div className="pt-1">
                  <p className="text-[11px] font-bold text-teal-200 uppercase tracking-wider mb-1">Next steps</p>
                  <ul className="space-y-1">
                    {grade.next_steps.slice(0, 5).map((s, i) => (
                      <li key={i} className="text-[11px] text-zinc-300 leading-relaxed"><span className="text-teal-400">→</span> {s}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
